/* eslint-disable react-hooks/rules-of-hooks */
import { Person, PersonThingRangeResponse, Thing, ThingType } from '@eagle/core-data-types';
import { CardHeader, IconButton, Menu, MenuItem, Stack, Typography } from '@ethings/ui-toolkit';
import { MouseEvent, useState, type JSX } from 'react';
import { useResponsiveText } from '../../hooks';
import { CacheDataTypes, Nullable, Portal, PortalTypes, Undefinable } from '../../types';
import { getMenuItems, innerText, switchToPortal } from '../../util';
import { FetchOne, FetchOneOfAll } from '../fetch';
import { useBoolFlag } from '../flags';
import { Icon } from '../icons';

interface DefaultProps {
  actions?: JSX.Element;
  excludePortals?: Portal[];
  checkFeatureFlag?: boolean;
  portal: Portal;
  title?: string;
}

interface Props<T extends Thing | PersonThingRangeResponse | Person> extends DefaultProps {
  'data-testid'?: string;
  entity: T;
  type?: typeof PortalTypes[keyof typeof PortalTypes];
}

export const PortalSwitchingDropdown = <T extends Thing | PersonThingRangeResponse | Person>({ actions, excludePortals = [], entity, type, title, portal: selectedPortal, checkFeatureFlag = true, ...props }: Props<T>): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);
  const portalFeature = checkFeatureFlag ? useBoolFlag('portals-portal-switching-component') : true;

  const getTitleLength = (): number => {
    if (title) {
      return typeof title === 'string' ? title.length : innerText(title).length;
    }
    return 20;
  };

  const h5Styles = useResponsiveText('1.5rem', getTitleLength(), {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  const allPortals = getMenuItems();
  const filteredPortals = allPortals.filter(({ portal }) => portal !== selectedPortal && !excludePortals?.includes(portal));
  const portalWithPermission = filteredPortals.some(({ permissions }) => permissions);

  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => setAnchorEl(event.currentTarget);
  const handleClose = (): void => setAnchorEl(null);
  const redirectToPortal = (portal: Portal): Undefinable<string> => {
    const entityId = type === PortalTypes.THING_PERSON_TYPE ? (entity as PersonThingRangeResponse).personId : (entity as Person | Thing)._id;
    return switchToPortal(selectedPortal, portal, `${type === PortalTypes.THING_TYPE ? 'thing' : 'person'}/${entityId}`);
  };

  const cardActions = (data: ThingType | Person): JSX.Element => <>
    {actions}
    {portalFeature && portalWithPermission
      && <>
        <IconButton onClick={handleClick} data-testid="portal-switching-button">
          <Icon icon="DotMenuVertical" />
        </IconButton>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          onClose={handleClose}
          open={open}
        >
          {filteredPortals.map(({ icon, permissions, portal, portalText, text, sx, ...rest }, i) => {
            if (!permissions) return <></>;
            return <MenuItem
              data-testid={`portal-switching-menu-item-${portal}`}
              key={`${i}`}
              onClick={() => {
                const redirect = redirectToPortal(portal);
                if (redirect) {
                  window.location.href = redirect;
                }
              }}
              {...rest}
              style={{ marginTop: 0, paddingLeft: '12px' }}
            >
              <>
                {icon}
                <Stack pl={1}>
                  <Typography>{text(data.display)}</Typography>
                  <Typography variant="body2" color="text.secondary">{portalText}</Typography>
                </Stack>
              </>
            </MenuItem>;
          })}
        </Menu>
      </>
    }
  </>;

  if (!entity) return <></>;

  const renderData = (data: ThingType | Person): JSX.Element => (
    <CardHeader
      action={cardActions(data)}
      title={
        <Typography
          variant="h5"
          textTransform="capitalize"
          {...props}
          style={{
            fontSize: h5Styles.fontSize,
            lineHeight: h5Styles.lineHeight,
            wordBreak: h5Styles.wordBreak,
            whiteSpace: h5Styles.whiteSpace,
            overflow: h5Styles.overflow,
            textOverflow: h5Styles.textOverflow,
          }}
        >
          {title ?? data.display}
        </Typography>
      }
    />
  );

  if (type !== CacheDataTypes.THING_TYPE) {
    return <FetchOne
      id={type === PortalTypes.THING_PERSON_TYPE ? (entity as PersonThingRangeResponse).personId : (entity as Person)._id}
      dataType={CacheDataTypes.PERSON}
      renderFactory={renderData}
    />;
  }

  return (
    <FetchOneOfAll
      id={(entity as Thing).thingTypeId}
      dataType={CacheDataTypes.THING_TYPE}
      renderFactory={renderData}
    />
  );
};
